import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/lib/format";
import { getProductImageUrl } from "@/lib/utils";
import { Plus, Minus, ShoppingCart, Check, ArrowLeft, ShieldCheck, Truck, Zap } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const [cep, setCep] = useState("");
  const [shippingResult, setShippingResult] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState("");

  const handleCepChange = (e) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 5) {
      val = val.substring(0, 5) + "-" + val.substring(5, 8);
    }
    setCep(val);
  };

  const handleCalculateShipping = () => {
    const cleanCep = cep.replace(/\D/g, "");
    if (cleanCep.length !== 8) {
      setShippingError("Informe um CEP válido com 8 dígitos.");
      setShippingResult(null);
      return;
    }
    setShippingLoading(true);
    setShippingError("");
    setShippingResult(null);
    api.post("/shipping/quote", {
      cep: cleanCep,
      items: [{ product_id: product.id, quantity: qty }]
    })
      .then((r) => {
        setShippingResult(r.data);
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || "Erro ao calcular o frete. Verifique o CEP.";
        setShippingError(msg);
      })
      .finally(() => {
        setShippingLoading(false);
      });
  };

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      const prod = r.data;
      setProduct(prod);
      if (prod.stock <= 0) {
        setQty(0);
      } else {
        setQty(1);
      }
    });
  }, [id]);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
        <div className="h-96 card-soft animate-pulse" />
      </div>
    );
  }

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} adicionado ao carrinho`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12">
      <Link
        to="/loja"
        data-testid="back-to-catalog"
        className="inline-flex items-center gap-2 font-quicksand text-sm font-semibold text-[#666] hover:text-[#1BB0CE] mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar ao catálogo
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="card-soft p-8">
          <div className="aspect-square bg-gp-gradient-light rounded-2xl overflow-hidden">
            <img src={getProductImageUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="card-soft p-8 sm:p-10 flex flex-col">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="bg-gp-gradient-light text-[#1BB0CE] px-3 py-1 rounded-full text-xs font-quicksand font-bold uppercase tracking-wider">
              {product.category}
            </span>
            {product.sku && (
              <span className="text-xs font-quicksand text-[#999]">SKU: {product.sku}</span>
            )}
          </div>

          <h1 className="font-quicksand text-3xl sm:text-4xl font-extrabold text-[#3d3d3d] leading-tight">
            {product.name}
          </h1>
          <p className="text-sm text-[#666] mt-4 leading-relaxed">{product.description}</p>

          {product.long_description && (
            <div className="bg-gp-gradient-light rounded-2xl p-4 mt-4">
              <p className="text-sm text-[#3d3d3d] leading-relaxed">{product.long_description}</p>
            </div>
          )}

          {product.specs && product.specs.length > 0 && (
            <div className="mt-8 mb-12">
              <div className="text-xs font-quicksand font-bold text-[#3d3d3d] uppercase tracking-wider mb-4">
                ESPECIFICAÇÕES
              </div>
              <ul className="space-y-2.5">
                {product.specs.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-gp-gradient-light flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#1BB0CE]" strokeWidth={3} />
                    </div>
                    <span className="text-[#3d3d3d]">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}



          <div className="mt-auto pt-8 border-t border-[#f1f1f3]">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-quicksand font-bold text-[#999]">Preço à vista (PIX)</div>
                <div className="font-quicksand text-4xl font-extrabold text-gradient-gp mt-1" data-testid="product-price">
                  {formatBRL(product.price * (qty > 0 ? qty : 1))}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider font-quicksand font-bold text-[#999]">Estoque</div>
                <div className="font-quicksand font-bold text-[#3d3d3d] mt-1">{product.stock} un.</div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <div className="flex items-center bg-white border-2 border-[#e4e4e7] rounded-full">
                <button 
                  onClick={() => setQty((q) => Math.max(product.stock > 0 ? 1 : 0, q - 1))} 
                  disabled={product.stock <= 0} 
                  data-testid="qty-decrease" 
                  className="p-3 hover:bg-[#f4f4f5] rounded-l-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="w-4 h-4 text-[#3d3d3d]" />
                </button>
                <span className="w-12 text-center font-quicksand font-bold text-[#3d3d3d]" data-testid="qty-value">{qty}</span>
                <button 
                  onClick={() => setQty((q) => Math.min(product.stock, q + 1))} 
                  disabled={product.stock <= 0 || qty >= product.stock} 
                  data-testid="qty-increase" 
                  className="p-3 hover:bg-[#f4f4f5] rounded-r-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 text-[#3d3d3d]" />
                </button>
              </div>
              <button 
                onClick={handleAdd} 
                disabled={product.stock <= 0 || qty <= 0} 
                data-testid="add-to-cart-button" 
                className="btn-gp flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {product.stock <= 0 ? (
                  "SEM ESTOQUE"
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4" /> ADICIONAR AO CARRINHO
                  </>
                )}
              </button>
            </div>

            {/* CALCULADORA DE FRETE */}
            <div className="mt-8 pt-6 border-t border-[#f1f1f3]">
              <div className="text-xs uppercase tracking-wider font-quicksand font-bold text-[#999] mb-3">
                Calcular Frete e Prazo
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  maxLength={9}
                  placeholder="00000-000"
                  value={cep}
                  onChange={handleCepChange}
                  className="border-2 border-[#e4e4e7] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#1BB0CE] font-quicksand font-bold text-[#3d3d3d] w-36"
                />
                <button
                  onClick={handleCalculateShipping}
                  className="btn-gp-outline rounded-full px-5 py-2 text-xs font-quicksand font-bold"
                  disabled={shippingLoading}
                >
                  {shippingLoading ? "Calculando..." : "Calcular"}
                </button>
              </div>

              {shippingError && (
                <p className="text-xs text-[#ea3838] mt-2 font-quicksand font-semibold">
                  {shippingError}
                </p>
              )}

              {shippingResult && (
                <div className="mt-4 space-y-2">
                  <div className="text-xs font-quicksand font-bold text-[#3d3d3d] mb-1">
                    Opções de frete para {shippingResult.city} - {shippingResult.uf}:
                  </div>
                  {shippingResult.options.map((opt) => (
                    <div key={opt.id} className="p-3 bg-gp-gradient-light rounded-xl flex items-center justify-between text-sm font-quicksand">
                      <div>
                        <span className="font-bold text-[#3d3d3d] block">
                          {opt.name}
                        </span>
                        <span className="text-[#666] text-xs block mt-0.5">
                          Prazo estimado: {opt.delivery_time} {opt.delivery_time === 1 ? "dia útil" : "dias úteis"}
                        </span>
                      </div>
                      <div className="text-right font-extrabold text-[#1BB0CE]">
                        {opt.cost === 0 ? (
                          <span className="text-[#00D9A6]">Frete Grátis</span>
                        ) : (
                          formatBRL(opt.cost)
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
